import { cloneDeep } from 'lodash';
import { v4 as uuid } from 'uuid';

import {
  EditorableSchema,
  FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY,
  FieldSchemaEditorSupportedTypes,
  FieldSchemaRelationSchema,
  RelationSchemaType,
} from 'dashboard/components-lib/FieldSchemaEditor/constants';
import { FieldSchema, FieldSchemaArray, FieldSchemaHash, FieldSchemaType } from 'lib/fields';
import { ApiModel } from 'lib/interfaces/api';
import { RelationSchema } from 'lib/relations';

type FormValues = Record<
  string,
  { key: string; path: string; fieldSchema: FieldSchemaEditorSupportedTypes }
>;

export const fieldSchemasToFormValues = (fieldSchemas: EditorableSchema[]): FormValues => {
  const fields = getAllFields(fieldSchemas);
  return fields.reduce((map, field) => {
    return {
      ...map,
      [field.key]: field,
    };
  }, {});
};

export const formValuesToFieldSchemas = (
  formValues: FormValues,
): FieldSchemaEditorSupportedTypes[] => {
  const keys = Object.values(formValues)
    // Ensures returned field schemas are always in order from first path to last path to respect original field schema array order
    .sort((a, b) => Number(a.path.split('.')[0]) - Number(b.path.split('.')[0]))
    .map((type) => type.key);

  return keys.reduce((schemas, key) => {
    if (key.includes('.')) {
      const { newSchemas } = replaceNestedField(
        schemas,
        formValues[key].path,
        formValues[key].fieldSchema,
      );
      return newSchemas;
    } else {
      return [...schemas, formValues[key].fieldSchema];
    }
  }, [] as FieldSchemaEditorSupportedTypes[]);
};

export const replaceNestedField = (
  originalSchemas: FieldSchemaEditorSupportedTypes[],
  path: string,
  newChild: FieldSchemaEditorSupportedTypes | null,
): { newSchemas: FieldSchemaEditorSupportedTypes[]; newPath: string } => {
  const tokens = path.split('.');

  if (tokens.length === 0) {
    return { newSchemas: originalSchemas, newPath: path };
  }
  const key = tokens[0];
  const newSchemas = originalSchemas
    .map((original, i) => {
      if (key === original.key || key === `${i}`) {
        const { newSchema, newKey } = replaceNestedFieldHelper(original, tokens.slice(1), newChild);
        tokens[tokens.length - 1] = newKey ? newKey : `${i}`;
        return newSchema;
      }
      return original;
    })
    .filter((schema): schema is FieldSchemaEditorSupportedTypes => !!schema);
  return { newSchemas, newPath: tokens.join('.') };
};

const replaceNestedFieldHelper = (
  originalSchema: FieldSchemaEditorSupportedTypes,
  tokens: string[],
  newChild: FieldSchemaEditorSupportedTypes | null,
): { newSchema: FieldSchemaEditorSupportedTypes | null; newKey?: string } => {
  if (tokens.length === 0) {
    return { newSchema: newChild, newKey: newChild?.key };
  }
  const key = tokens[0];
  let updatedKey = tokens.slice(-1)[0];
  if (originalSchema.type === FieldSchemaType.Hash) {
    const newItemSchemas = originalSchema.config['item-schemas']
      .map((original, i) => {
        if (key === original.key || key === `${i}`) {
          const { newSchema, newKey } = replaceNestedFieldHelper(
            original,
            tokens.slice(1),
            newChild,
          );
          updatedKey = newKey ? newKey : `${i}`;
          return newSchema;
        }
        return original;
      })
      .filter((schema) => !!schema);
    const newSchema = {
      ...originalSchema,
      config: {
        ...originalSchema.config,
        'item-schemas': newItemSchemas,
      },
    } as FieldSchemaHash;
    return { newSchema, newKey: updatedKey };
  }

  if (key === '[]' && originalSchema.type === FieldSchemaType.Array) {
    const { newSchema, newKey } = replaceNestedFieldHelper(
      originalSchema.config['item-schema'],
      tokens.slice(1),
      newChild,
    );
    updatedKey = newKey ? newKey : key;
    const updatedSchema = {
      ...originalSchema,
      config: {
        ...originalSchema.config,
        'item-schema': newSchema,
      },
    } as FieldSchemaArray;
    return {
      newSchema: updatedSchema,
      newKey: updatedKey,
    };
  }
  return { newSchema: originalSchema, newKey: updatedKey };
};

/**
 * The key of the field is the full path to itself from the top level
 * If a field has a valid key, then the path will use the key
 * If a field does not have a valid key (such as when it is just created),
 * then the path will use the index of the field within its parent
 *
 * For example, abc.def and abc.2 could both present the third child field of abc
 *
 * If the item-schema of an array is a hash, the key is always empty.
 * In this case, the key to the item-schema is taken to be "[]"
 */
export const getAllFields = (
  fieldSchemas: FieldSchemaEditorSupportedTypes[],
  prefix = '',
  pathPrefix = '',
): { key: string; path: string; fieldSchema: FieldSchemaEditorSupportedTypes }[] => {
  // Not using record because this method is used for checking duplicate keys
  prefix = prefix ? `${prefix}.` : '';
  pathPrefix = pathPrefix ? `${pathPrefix}.` : '';
  if (!fieldSchemas) {
    return [];
  }
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return fieldSchemas.reduce((acc, schema, i) => {
    const currentKey = `${prefix}${schema.key ? schema.key : i}`;
    const currentPath = `${pathPrefix}${i}`;
    const entry = { key: currentKey, path: currentPath, fieldSchema: schema };
    if (schema.type === FieldSchemaType.Hash) {
      return [
        ...acc,
        entry,
        ...getAllFields(
          schema.config['item-schemas'],
          currentKey,
          `${currentPath}.config.item-schemas`,
        ),
      ];
    }

    if (
      schema.type === FieldSchemaType.Array &&
      schema.config['item-schema']?.type === FieldSchemaType.Hash
    ) {
      return [
        ...acc,
        entry,
        {
          key: `${currentKey}.[]`,
          path: `${currentPath}.config.item-schema`,
          fieldSchema: schema.config['item-schema'],
        },
        ...getAllFields(
          schema.config['item-schema'].config['item-schemas'],
          `${currentKey}.[]`,
          `${currentPath}.config.item-schema.config.item-schemas`,
        ),
      ];
    }
    return [...acc, entry];
  }, []);
};

export const getAllFieldKeys = (fieldSchemas: FieldSchemaEditorSupportedTypes[]): string[] => {
  return getAllFields(fieldSchemas).map(({ key }) => key);
};

export const getAllRequirePresenceConfigs = (
  fieldSchemas: FieldSchemaEditorSupportedTypes[],
): boolean[] => {
  return getAllFields(fieldSchemas).map((field) => field.fieldSchema.config?.required);
};

export const moveFieldWithinSchema = (
  fieldSchemas: FieldSchemaEditorSupportedTypes[],
  sourceKey: string,
  destinationKey: string,
): { updatedSchemas: FieldSchemaEditorSupportedTypes[]; finalDestinationKey: string } => {
  let fieldSchemaCopy = cloneDeep(fieldSchemas);
  const allFields = getAllFields(fieldSchemaCopy);
  if (
    !destinationKey ||
    sourceKey === destinationKey ||
    destinationKey.startsWith(sourceKey) ||
    allFields.every(({ key }) => key !== sourceKey) ||
    allFields.every(({ key }) => key !== destinationKey)
  ) {
    return { updatedSchemas: fieldSchemas, finalDestinationKey: destinationKey };
  }

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const fieldToRemove = allFields.find(({ key }) => key === sourceKey).fieldSchema;
  const fieldToAdd = cloneDeep(fieldToRemove);
  const originalBaseKey = fieldToAdd.key;
  fieldToAdd.key = uuid(); // make sure the moved field does not have key conflict with the original field

  const destinationParentKey = destinationKey.split('.').slice(0, -1).join('.');
  const destinationBaseKey = destinationKey.split('.').slice(-1)[0];

  function insertAfter(
    originalSchema: FieldSchemaEditorSupportedTypes[],
    fieldToInsert = fieldToAdd,
    baseKey = destinationBaseKey,
  ): FieldSchemaEditorSupportedTypes[] {
    const newSchemas: FieldSchemaEditorSupportedTypes[] = [];
    originalSchema.forEach((schema, i) => {
      newSchemas.push(schema);
      if (baseKey === schema.key || baseKey === `${i}`) {
        newSchemas.push(fieldToInsert);
      }
    });
    return newSchemas;
  }

  if (destinationParentKey) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const parentSchema = allFields.find(({ key }) => key === destinationParentKey).fieldSchema;
    if (parentSchema.type === FieldSchemaType.Hash) {
      parentSchema.config['item-schemas'] = insertAfter(
        parentSchema.config['item-schemas'],
      ) as FieldSchema[];
    }
  } else {
    fieldSchemaCopy = insertAfter(fieldSchemaCopy);
  }

  // delete the original field
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const finalRemovedKey = getAllFields(fieldSchemaCopy).find(
    ({ fieldSchema }) => fieldSchema === fieldToRemove,
  ).key;
  const { newSchemas: updatedSchemas } = replaceNestedField(fieldSchemaCopy, finalRemovedKey, null);

  // recalculate the moved field's key
  fieldToAdd.key = originalBaseKey;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const finalDestinationKey = getAllFields(updatedSchemas).find(
    ({ fieldSchema }) => fieldSchema === fieldToAdd,
  ).key;

  return { updatedSchemas, finalDestinationKey };
};

export function convertRelationToFieldSchema(
  relationSchemas: RelationSchema[],
): FieldSchemaRelationSchema[] {
  return relationSchemas.map((schema) => ({
    type: RelationSchemaType.RelationSchema,
    token: schema.token,
    key: schema.key,
    label: schema.label,
    cardinality: schema.cardinality,
    'inverse-key': schema['inverse-key'],
    'inverse-label': schema['inverse-label'],
    'inverse-cardinality': schema['inverse-cardinality'],
    config: {},
  }));
}

export function convertFieldToRelationSchema(
  fieldSchemas: FieldSchemaRelationSchema[],
): RelationSchema[] {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return fieldSchemas.map((schema) => ({
    token: schema.token,
    type: schema.token.startsWith('acttp')
      ? ApiModel.AccountType
      : schema.token.startsWith('txntp')
        ? ApiModel.TransactionType
        : null,
    key: schema.key,
    label: schema.label,
    cardinality: schema.cardinality,
    'inverse-key': schema['inverse-key'],
    'inverse-label': schema['inverse-label'],
    'inverse-cardinality': schema['inverse-cardinality'],
  }));
}

export function isTopLevelField(fieldSchemaPath: string): boolean {
  return fieldSchemaPath.split('.').length === 1;
}

export const fieldTypeFilterOptions = (fieldTypes: Array<FieldSchemaType | RelationSchemaType>) =>
  fieldTypes
    .map((fieldType: FieldSchemaType | RelationSchemaType) => ({
      label: FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY[fieldType] ?? '',
      value: fieldType,
    }))
    .filter(({ label }) => label !== '')
    .sort(({ label: a }, { label: b }) => a.localeCompare(b));
